import type { RouterOptions } from '@tanstack/react-router';

import { isStartBusinessTypePathname } from 'router/params/startBusinessType';
import type { RoutePaths } from 'router/types';
import { prefersReducedMotion } from 'utils/browser';

type DefaultViewTransitionTypesFn = Extract<
  Extract<
    NonNullable<RouterOptions<never, never>['defaultViewTransition']>,
    { types: unknown }
  >['types'],
  (...args: never[]) => unknown
>;

export type RouterLocationChangeInfo =
  Parameters<DefaultViewTransitionTypesFn>[0];

/**
 * `false` is an instant swap. A navigation is either forward or backward,
 * never both, so a verdict carries at most one type token.
 */
export type ViewTransitionVerdict<TransitionType extends string = string> =
  | [TransitionType]
  | false;

const WELCOME_PATHNAME = '/' satisfies RoutePaths;

export type DirectionalViewTransitionTypes = Record<
  'forward' | 'backward',
  string
>;

export const WELCOME_VIEW_TRANSITION_TYPE = {
  forward: 'welcome-to-business-type',
  backward: 'business-type-to-welcome',
} as const satisfies DirectionalViewTransitionTypes;

export type WelcomeViewTransitionType =
  (typeof WELCOME_VIEW_TRANSITION_TYPE)[keyof typeof WELCOME_VIEW_TRANSITION_TYPE];

export function resolveWelcomeViewTransition({
  fromLocation,
  toLocation,
}: RouterLocationChangeInfo): ViewTransitionVerdict<WelcomeViewTransitionType> {
  if (!fromLocation) return false;

  if (
    fromLocation.pathname === WELCOME_PATHNAME &&
    isStartBusinessTypePathname(toLocation.pathname)
  ) {
    return [WELCOME_VIEW_TRANSITION_TYPE.forward];
  }

  if (
    isStartBusinessTypePathname(fromLocation.pathname) &&
    toLocation.pathname === WELCOME_PATHNAME
  ) {
    return [WELCOME_VIEW_TRANSITION_TYPE.backward];
  }

  return false;
}

// No CSS keys off this token: the cross-fade is the user-agent (UA) default
// animation, whose complementary fade curves keep pixels identical on both
// pages (the header, the actions bar) perfectly still. The token makes the
// verdict truthy (which is what starts the transition) and is the hook for
// `:active-view-transition-type` scoping if tuning is ever needed.
export const APP_VIEW_TRANSITION_TYPE = {
  crossFade: 'app-cross-fade',
} as const satisfies Record<'crossFade', string>;

export type AppViewTransitionType =
  (typeof APP_VIEW_TRANSITION_TYPE)[keyof typeof APP_VIEW_TRANSITION_TYPE];

function isPageToPageNavigation({
  fromLocation,
  pathChanged,
}: RouterLocationChangeInfo): boolean {
  return Boolean(fromLocation) && pathChanged;
}

export function resolveViewTransition(
  locationChangeInfo: RouterLocationChangeInfo,
  prefersReducedMotionValue: boolean,
): ViewTransitionVerdict<WelcomeViewTransitionType | AppViewTransitionType> {
  if (prefersReducedMotionValue) return false;
  if (!isPageToPageNavigation(locationChangeInfo)) return false;

  const welcomeViewTransition =
    resolveWelcomeViewTransition(locationChangeInfo);
  if (welcomeViewTransition) return welcomeViewTransition;

  return [APP_VIEW_TRANSITION_TYPE.crossFade];
}

export const makeDefaultViewTransitionTypes: DefaultViewTransitionTypesFn =
  locationChangeInfo =>
    resolveViewTransition(locationChangeInfo, prefersReducedMotion());
